import { type Transition, type Variants } from 'framer-motion';

// ── Duration Standard ──
export const duration = {
  instant: 0.1,    // Micro-interactions (tooltip, hover)
  fast: 0.2,       // UI feedback (button press, toggle)
  normal: 0.3,     // Standard transitions (fade, color)
  smooth: 0.5,     // Layout transitions (stage changes)
  emphasis: 0.8,   // Attention animations (progress bars, scores)
} as const;

// ── Easing Standard ──
export const easing = {
  standard: [0.4, 0, 0.2, 1] as const,      // Material Standard — default for most transitions
  decelerate: [0, 0, 0.2, 1] as const,       // Elements entering the screen
  accelerate: [0.4, 0, 1, 1] as const,       // Elements leaving the screen
  expressive: [0.22, 1, 0.36, 1] as const,   // Dramatic transitions (stage changes)
  bounce: [0.34, 1.56, 0.64, 1] as const,    // Celebratory / success
} as const;

// ── Spring Physics ──
export const spring = {
  gentle: { type: 'spring', stiffness: 120, damping: 20, mass: 0.8 } as Transition,
  snappy: { type: 'spring', stiffness: 300, damping: 30 } as Transition,
  bouncy: { type: 'spring', stiffness: 400, damping: 25, mass: 0.5 } as Transition,
};

// ── Tween Presets ──
export const ease = {
  smooth: { duration: duration.normal, ease: easing.standard } as Transition,
  enter: { duration: 0.25, ease: easing.decelerate } as Transition,
  exit: { duration: 0.15, ease: easing.accelerate } as Transition,
};

// ── Reusable Variants ──

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: ease.enter },
};

export const fadeSlideUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: spring.gentle },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: spring.snappy },
};

export const staggerContainer = (staggerDelay = 0.06): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: staggerDelay } },
});

// ── Stage Transition (layout changes, e.g. Stage 2 → 3) ──
export const stageTransition = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, scale: 0.98 },
  transition: { duration: duration.smooth, ease: easing.expressive },
};

// ── Collapse / Expand (accordion, product detail rows) ──
export const collapseTransition = {
  duration: duration.normal,
  ease: easing.standard,
};

export const collapse: Variants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1, transition: collapseTransition },
  exit: { height: 0, opacity: 0, transition: collapseTransition },
};

// ── Page Transition ──
export const pageTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: duration.fast, ease: easing.decelerate },
};

// ── Wizard Step (directional slide) ──
export const wizardStep = (direction: 1 | -1): Variants => ({
  enter: { x: direction * 60, opacity: 0 },
  center: { x: 0, opacity: 1, transition: spring.snappy },
  exit: { x: direction * -60, opacity: 0, transition: ease.exit },
});

// ── Celebration ──
export const celebrationBounce: Variants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: spring.bouncy,
  },
};

export const sparkleConfig = {
  dotCount: 6,
  duration: 1.4,
  easing: 'ease-out',
} as const;
